import React, { useState, useEffect } from "react";
import { AddButton, SaveButton } from "../../../components/btn/Buttons";
import PageHeading from "../../../components/PageHeading";
import Table from "../../../components/table/Table";
import { DeleteIcon, base_url } from "../../../utils/utils";
import Input from "../../../components/inputs/Input";
import moment from "moment";
import Dropdown from "../../../components/inputs/Dropdown";
import SmallModal from "../../../components/modal/SmallModal";
import { Pagination2 } from "../../../components/pagination/pagination";
import { useSelector } from "react-redux";

const PartnersTransaction = () => {
  const [open, setOpen] = useState(false);
  const [addProductData, setAddProductData] = useState([]);
  const [alerts, setAlerts] = useState(false);
  const [alertsMess, setAlertsMess] = useState("");
  const [loading, setLoading] = useState(false);
  const [partnersListData, setPartnersListData] = useState([]);
  const [banksListData, setBanksListData] = useState([]);
  const [transactionListData, setTransactionListData] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [entries, setEntries] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const { user_id, emp_id, user_type } = useSelector((state) => state.other);

  // Get current financial year start date (April 1st)
  const getFinancialYearStartDate = () => {
    const currentDate = moment();
    const currentYear = currentDate.year();

    // If current month is before April, FY started last year
    if (currentDate.month() < 3) {
      // 0-indexed, so 3 = April
      return moment(`${currentYear - 1}-04-01`, "YYYY-MM-DD");
    }
    return moment(`${currentYear}-04-01`, "YYYY-MM-DD");
  };

  // Get max date (today - prevent future dates)
  const getMaxDate = () => {
    return moment().format("YYYY-MM-DD");
  };

  const fyStartDate = getFinancialYearStartDate().format("YYYY-MM-DD");
  const maxDate = getMaxDate();

  // Validate if date is within current financial year and not in future
  const isValidTransactionDate = (dateString) => {
    if (!dateString) return false;

    const transactionDate = moment(dateString, "YYYY-MM-DD");
    const fyStart = moment(fyStartDate, "YYYY-MM-DD");
    const today = moment(maxDate, "YYYY-MM-DD");

    // Check if date is within financial year (on or after April 1st)
    if (transactionDate.isBefore(fyStart, "day")) {
      return false;
    }

    // Check if date is not in future (on or before today)
    if (transactionDate.isAfter(today, "day")) {
      return false;
    }

    return true;
  };

  // Get agency ID from localStorage
  const getAgencyId = () => {
    const userIdString = localStorage.getItem("userId");
    if (userIdString) {
      const userData = JSON.parse(userIdString);
      return userData?.aid;
    }
    return null;
  };

  // Filter banks based on payment mode (account type)
  const getFilteredBanks = (paymentMode) => {
    if (!paymentMode) return banksListData;

    if (paymentMode == "1") {
      // Cash mode - show only CASH account type
      return banksListData.filter(
        (bank) => bank.ACCOUNT_TYPE?.toUpperCase() == "CASH"
      );
    } else if (paymentMode == "2") {
      // Online mode - show only OMC (Online) account type
      return banksListData.filter(
        (bank) => bank.ACCOUNT_TYPE?.toUpperCase() == "OMC"
      );
    }

    return banksListData;
  };

  // Fetch partners list
  const fetchPartnersList = async () => {
    try {
      const agencyId = getAgencyId();
      if (!agencyId) return;

      const formData = new FormData();
      formData.append("action", "active_partners");
      formData.append("agencyid", agencyId);

      const response = await fetch(
        `${base_url}financial_reports/partner_data.php`,
        { method: "POST", body: formData }
      );
      const result = await response.json();

      if (result.success && result.data) {
        setPartnersListData(result.data);
      }
    } catch (error) {
      console.error("Error fetching partners:", error);
    }
  };

  // Fetch banks list
  const fetchBanksList = async () => {
    try {
      const agencyId = getAgencyId();
      if (!agencyId) return;

      const formData = new FormData();
      formData.append("action", "list");
      formData.append("agencyid", agencyId);
      formData.append("page", 1);
      formData.append("limit", 1000);

      const response = await fetch(`${base_url}bank_master.php`, {
        method: "POST",
        body: formData,
      });
      const result = await response.json();
      if (result.success && result.data) {
        setBanksListData(result.data);
      }
    } catch (error) {
      console.error("Error fetching banks:", error);
    }
  };

  // Fetch transactions list
  const fetchTransactionsList = async (page = 1) => {
    try {
      setListLoading(true);
      const agencyId = getAgencyId();
      if (!agencyId) {
        setListLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("action", "list");
      formData.append("agencyid", agencyId);
      formData.append("page", page);
      formData.append("limit", parseInt(entries));

      const response = await fetch(
        `${base_url}financial_reports/partner_transaction.php`,
        { method: "POST", body: formData }
      );
      const result = await response.json();

      if (result.success && result.data) {
        setTransactionListData(result.data);
        setTotalCount(result.totalcount || 0);
        setPageNo(page);
      } else {
        setTransactionListData([]);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setListLoading(false);
    }
  };

  // Submit transaction to API
  const submitTransaction = async (transactionData) => {
    try {
      setLoading(true);
      const agencyId = getAgencyId();

      if (!agencyId) {
        setAlerts(true);
        setAlertsMess(
          <div style={{ color: "red", textAlign: "left" }}>
            <p>Error: Agency ID not found</p>
          </div>
        );
        return false;
      }

      // Get partner details
      const partner = partnersListData.find(
        (p) => p.ID === parseInt(transactionData.PARTNER_NAME)
      );

      const formData = new FormData();
      formData.append("action", "create");
      formData.append("agencyid", agencyId);
      formData.append("partner_id", transactionData.PARTNER_NAME);
      formData.append(
        "bank_id",
        transactionData.PAYMENT_MODE == "1" ? "0" : transactionData.BANK
      );
      formData.append("transaction_date", transactionData.TRANSACTION_DATE);
      formData.append("transaction_type", transactionData.TRANSACTION_TYPE);
      formData.append("transaction_amount", transactionData.TRANSACTION_AMOUNT);
      formData.append("remark", transactionData.NARRATION);
      formData.append("payment_mode", transactionData.PAYMENT_MODE);
      formData.append("emp_code", emp_id || "0");

      const response = await fetch(
        `${base_url}financial_reports/partner_transaction.php`,
        { method: "POST", body: formData }
      );

      const result = await response.json();

      if (result.success) {
        setAlerts(true);
        setAlertsMess(
          <div style={{ color: "green", textAlign: "left" }}>
            <p>{result.message || "Transaction saved successfully!"}</p>
          </div>
        );
        return true;
      } else {
        setAlerts(true);
        setAlertsMess(
          <div style={{ color: "red", textAlign: "left" }}>
            <p>{result.message || "Error saving transaction"}</p>
          </div>
        );
        return false;
      }
    } catch (error) {
      console.error("Error submitting transaction:", error);
      setAlerts(true);
      setAlertsMess(
        <div style={{ color: "red", textAlign: "left" }}>
          <p>Error: {error.message}</p>
        </div>
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchPartnersList();
    fetchBanksList();
    fetchTransactionsList();
  }, []);

  const valiDate = () => {
    const errors = [];
    let count = 1;
    for (let v of addProductData) {
      if (!v?.TRANSACTION_DATE) {
        errors.push(`Please select Transaction Date in Row ${count}`);
      } else if (!isValidTransactionDate(v.TRANSACTION_DATE)) {
        const fyStartFormatted = moment(fyStartDate).format("DD/MM/YYYY");
        const todayFormatted = moment(maxDate).format("DD/MM/YYYY");
        errors.push(
          `Transaction Date must be between ${fyStartFormatted} and ${todayFormatted} (Current Financial Year only) in Row ${count}`
        );
      }
      if (!v?.PARTNER_NAME) {
        errors.push(`Please select Partner Name in Row ${count}`);
      }
      if (!v?.TRANSACTION_TYPE) {
        errors.push(`Please select Transaction Type in Row ${count}`);
      }
      if (!v?.TRANSACTION_AMOUNT || v?.TRANSACTION_AMOUNT.trim().length === 0) {
        errors.push(`Please enter Transaction Amount in Row ${count}`);
      }
      if (!v?.BANK) {
        errors.push(`Please select Bank in Row ${count}`);
      }
      if (!v?.NARRATION || v?.NARRATION.trim().length === 0) {
        errors.push(`Please enter Narration in Row ${count}`);
      }
      if (!v?.PAYMENT_MODE) {
        errors.push(`Please select Payment Mode in Row ${count}`);
      }
      count++;
    }
    return errors;
  };

  const handleAddProduct = () => {
    const defaultProduct = {
      id: addProductData.length + 1,
      TRANSACTION_DATE: "",
      PARTNER_NAME: "",
      TRANSACTION_TYPE: "",
      TRANSACTION_AMOUNT: "",
      BANK: "",
      NARRATION: "",
      PAYMENT_MODE: "",
      ACTIONS: "",
    };

    if (!addProductData.length || valiDate().length === 0) {
      setAddProductData((prevData) => [...prevData, defaultProduct]);
    } else {
      const validationErrors = valiDate();
      setAlerts(true);
      setAlertsMess(
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
          }}
        >
          {validationErrors.map((item, i) => (
            <p style={{ textAlign: "left" }} key={i}>
              {item}
            </p>
          ))}
        </div>
      );
    }
  };

  const handleSaveProduct = async () => {
    const validationErrors = valiDate();

    if (validationErrors.length > 0) {
      setAlerts(true);
      setAlertsMess(
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
          }}
        >
          {validationErrors.map((item, i) => (
            <p style={{ textAlign: "left" }} key={i}>
              {item}
            </p>
          ))}
        </div>
      );
      return;
    }

    let successCount = 0;
    let failureCount = 0;

    for (const transaction of addProductData) {
      const success = await submitTransaction(transaction);
      if (success) {
        successCount++;
      } else {
        failureCount++;
      }
    }

    if (failureCount === 0 && successCount > 0) {
      setAlerts(true);
      setAlertsMess(
        <div style={{ color: "green", textAlign: "left" }}>
          <p>✓ All {successCount} transaction(s) saved successfully!</p>
        </div>
      );
      setTimeout(() => {
        setAddProductData([]);
        setOpen(false);
        fetchTransactionsList(1);
      }, 2000);
    } else if (successCount > 0 && failureCount > 0) {
      setAlerts(true);
      setAlertsMess(
        <div style={{ color: "orange", textAlign: "left" }}>
          <p>
            ⚠ {successCount} transaction(s) saved, but {failureCount} failed
          </p>
        </div>
      );
    }
  };

  const handleAddDelete = (deleteId) => {
    const filterData = addProductData.filter((item) => item.id !== deleteId);
    setAddProductData(filterData);
  };
  return (
    <div className="container">
      <PageHeading
        title={"PARTNERS TRANSACTIONS"}
        right={
          <>
            {!open && (
              <AddButton
                onClick={() => {
                  setOpen(true);
                  handleAddProduct();
                }}
                disabled={loading}
                style={loading ? { opacity: 0.5, cursor: "not-allowed" } : {}}
              />
            )}
            {open && (
              <button
                className="GENERATE-BTN"
                onClick={() => {
                  setOpen(false);
                  setAddProductData([]);
                }}
                style={{ backgroundColor: "#e74c3c" }}
              >
                CANCEL
              </button>
            )}
            <SaveButton
              title={loading ? "SAVING..." : "SAVE"}
              onClick={handleSaveProduct}
              disabled={loading || !addProductData.length}
            />
          </>
        }
      />
      {open && (
        <>
          <Table
            mt={30}
            headData={[
              "TRANSACTION DATE",
              "PARTNER NAME",
              "TRANSACTION TYPE",
              "TRANSACTION AMOUNT",
              "PAYMENT MODE",
              "BANK",
              "NARRATION",
              "ACTIONS",
            ]}
            body={
              <>
                {addProductData.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <Input
                        type={"date"}
                        value={item.TRANSACTION_DATE}
                        min={fyStartDate}
                        max={maxDate}
                        onChange={(event) => {
                          setAddProductData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              TRANSACTION_DATE: event.target.value,
                            };
                            return updatedData;
                          });
                        }}
                      />
                    </td>
                    <td>
                      <Dropdown
                        value={item.PARTNER_NAME}
                        onChange={(e) => {
                          setAddProductData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              PARTNER_NAME: e.target.value,
                            };
                            return updatedData;
                          });
                        }}
                        options={
                          <>
                            <option value="">SELECT PARTNER</option>
                            {partnersListData.map((partner) => (
                              <option key={partner.ID} value={partner.ID}>
                                {partner.PARTNER_NAME}
                              </option>
                            ))}
                          </>
                        }
                      />
                    </td>
                    <td>
                      <Dropdown
                        value={item.TRANSACTION_TYPE}
                        onChange={(e) => {
                          setAddProductData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              TRANSACTION_TYPE: e.target.value,
                            };
                            return updatedData;
                          });
                        }}
                        options={
                          <>
                            <option value="">SELECT TYPE</option>
                            <option value="1">Capital Withdrawal</option>
                            <option value="2">Capital Addition</option>
                            <option value="3">Exit Payout</option>
                          </>
                        }
                      />
                    </td>
                    <td>
                      <Input
                        placeholder={"AMOUNT"}
                        type="number"
                        value={item.TRANSACTION_AMOUNT}
                        onWheel={(e) => e.target.blur()}
                        onChange={(event) => {
                          setAddProductData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              TRANSACTION_AMOUNT: event.target.value,
                            };
                            return updatedData;
                          });
                        }}
                      />
                    </td>
                    <td>
                      <Dropdown
                        value={item.PAYMENT_MODE}
                        onChange={(e) => {
                          setAddProductData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              PAYMENT_MODE: e.target.value,
                              BANK: "",
                            };
                            return updatedData;
                          });
                        }}
                        options={
                          <>
                            <option value="">SELECT MODE</option>
                            <option value="1">Cash</option>
                            <option value="2">Online</option>
                          </>
                        }
                      />
                    </td>
                    <td>
                      <Dropdown
                        value={item.BANK}
                        onChange={(e) => {
                          setAddProductData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              BANK: e.target.value,
                            };
                            return updatedData;
                          });
                        }}
                        disabled={!item.PAYMENT_MODE}
                        options={
                          <>
                            <option value="">SELECT BANK</option>
                            {/* {getFilteredBanks(item.PAYMENT_MODE).map((bank) => (
                              <option key={bank.ID} value={bank.ID}>
                                {bank.BANK_CODE || bank.NAME}
                              </option>
                            ))} */}
                            {getFilteredBanks(item.PAYMENT_MODE).map((bank) => {
                              const bankCode = bank.BANK_CODE || "";
                              const nickName =
                                bank.NICK_NAME || bank.BANK_NAME || "";
                              const accountNo = bank.BANK_ACC_NO || "";
                              const last4Digits = accountNo.slice(-4);
                              const displayLabel = `${bankCode}-${nickName}-${last4Digits}`;
                              return (
                                <option key={bank.ID} value={bank.ID}>
                                  {displayLabel}
                                </option>
                              );
                            })}
                          </>
                        }
                      />
                    </td>
                    <td>
                      <Input
                        placeholder={"NARRATION"}
                        value={item.NARRATION}
                        onChange={(event) => {
                          setAddProductData((prevData) => {
                            const updatedData = [...prevData];
                            updatedData[index] = {
                              ...updatedData[index],
                              NARRATION: event.target.value,
                            };
                            return updatedData;
                          });
                        }}
                      />
                    </td>
                    <td>
                      <DeleteIcon onClick={() => handleAddDelete(item.id)} />
                    </td>
                  </tr>
                ))}
              </>
            }
          />
        </>
      )}
      <div style={{ height: "10px" }}></div>
      <div>
        <Table
          headData={[
            "PARTNER NAME",
            "TRANSACTION DATE",
            "TRANSACTION TYPE",
            "TRANSACTION AMOUNT",
            "PAYMENT MODE",
            "BANK",
            "NARRATION",
            // "STATUS",
          ]}
          body={
            <>
              {transactionListData && transactionListData.length > 0 ? (
                transactionListData.map((item, index) => {
                  const partner = partnersListData.find(
                    (p) => p.ID === item.PARTNER_ID
                  );
                  const bank = banksListData.find((b) => b.ID === item.BANK_ID);

                  const transactionTypeMap = {
                    1: "Capital Withdrawal",
                    2: "Capital Addition",
                    3: "Exit Payout",
                  };

                  const paymentModeMap = {
                    1: "Cash",
                    2: "Online",
                  };

                  return (
                    <tr key={index}>
                      <td>{item?.PARTNER_NAME || "N/A"}</td>
                      <td>{moment(item.TRANX_DATE).format("DD/MM/YYYY")}</td>
                      <td>{transactionTypeMap[item.TRANX_TYPE] || "N/A"}</td>
                      <td>₹{parseFloat(item.TRANX_AMOUNT).toLocaleString()}</td>
                      <td>{paymentModeMap[item.PAYMENT_MODE] || "N/A"}</td>
                      <td>{item?.BANK_CODE || "N/A"}</td>
                      <td>{item.REMARKS}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    {listLoading ? "Loading..." : "No transactions found"}
                  </td>
                </tr>
              )}
            </>
          }
        />
      </div>
      <Pagination2
        top={true}
        bottom={true}
        totalCount={totalCount}
        pageNo={pageNo}
        setPageNo={(pageNumber) => {
          setPageNo(pageNumber);
          fetchTransactionsList(pageNumber);
        }}
        entries={entries}
        setEntries={setEntries}
        search={false}
        count={5}
      />
      {alerts && (
        <SmallModal
          isOpen={alerts}
          setIsOpen={setAlerts}
          title={"alert"}
          mess={alertsMess}
          okbtn={setAlertsMess}
        />
      )}
    </div>
  );
};

export default PartnersTransaction;
